/**
 * Inactivity Detector
 * Tracks user activity and triggers logout after specified idle time
 * Supports cross-tab synchronization using localStorage
 */

class InactivityDetector {
  constructor(options = {}) {
    this.timeout = options.timeout || 30 * 60 * 1000; // Default: 30 minutes
    this.onInactive = options.onInactive || (() => {});
    this.warningTime = options.warningTime || 5 * 60 * 1000; // Warning 5 minutes before logout
    this.onWarning = options.onWarning || (() => {});
    this.onActivity = options.onActivity || (() => {});
    
    this.timeoutId = null;
    this.warningTimeoutId = null;
    this.lastActivityTime = Date.now();
    this.isWarningShown = false;
    
    // Events to track user activity
    this.events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];
    
    this.handleActivity = this.handleActivity.bind(this);
    this.handleStorageChange = this.handleStorageChange.bind(this);
    this.checkActivity = this.checkActivity.bind(this);
  }

  /**
   * Initialize the inactivity detector
   */
  start() {
    // Add event listeners for user activity
    this.events.forEach(event => {
      document.addEventListener(event, this.handleActivity, true);
    });
    
    // Listen for storage changes (cross-tab communication)
    window.addEventListener('storage', this.handleStorageChange);
    
    // Set initial activity time
    this.updateActivityTime();
    
    // Start the inactivity timer
    this.resetTimer();
    
    // Periodically check activity (helpful for cross-tab sync)
    this.checkInterval = setInterval(this.checkActivity, 1000);
  }

  /**
   * Stop the inactivity detector
   */
  stop() {
    // Remove event listeners
    this.events.forEach(event => {
      document.removeEventListener(event, this.handleActivity, true);
    });
    
    window.removeEventListener('storage', this.handleStorageChange);
    
    // Clear timers
    this.clearTimers();
    
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
  }

  /**
   * Handle user activity
   */
  handleActivity() {
    this.updateActivityTime();
    this.resetTimer();
    
    // Reset warning state and call onActivity callback
    if (this.isWarningShown) {
      this.isWarningShown = false;
      this.onActivity();
    }
  }

  /**
   * Update last activity time in localStorage for cross-tab sync
   */
  updateActivityTime() {
    this.lastActivityTime = Date.now();
    localStorage.setItem('lastActivityTime', this.lastActivityTime.toString());
  }

  /**
   * Reset the inactivity timer
   */
  resetTimer() {
    this.clearTimers();
    
    // Set warning timer (if warning callback is provided)
    const timeUntilWarning = this.timeout - this.warningTime;
    if (timeUntilWarning > 0 && this.onWarning) {
      this.warningTimeoutId = setTimeout(() => {
        if (!this.isWarningShown) {
          this.isWarningShown = true;
          this.onWarning(Math.floor(this.warningTime / 1000));
        }
      }, timeUntilWarning);
    }
    
    // Set logout timer
    this.timeoutId = setTimeout(() => {
      this.triggerInactive();
    }, this.timeout);
  }

  /**
   * Clear all timers
   */
  clearTimers() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = null;
    }
  }

  /**
   * Trigger inactive callback
   */
  triggerInactive() {
    // Mark as inactive in localStorage for other tabs
    localStorage.setItem('userInactive', Date.now().toString());
    this.onInactive();
  }

  /**
   * Handle storage changes from other tabs
   */
  handleStorageChange(event) {
    // Check if activity was detected in another tab
    if (event.key === 'lastActivityTime') {
      const newActivityTime = parseInt(event.newValue);
      if (newActivityTime > this.lastActivityTime) {
        this.lastActivityTime = newActivityTime;
        this.resetTimer();
        
        // Reset warning if shown and call onActivity callback
        if (this.isWarningShown) {
          this.isWarningShown = false;
          this.onActivity();
        }
      }
    }
    
    // Check if user became inactive in another tab
    if (event.key === 'userInactive') {
      this.onInactive();
    }
    
    // Check if user logged out in another tab
    if (event.key === 'accessToken' && !event.newValue) {
      // Access token was removed, user logged out
      this.stop();
    }
  }

  /**
   * Periodically check if user has been inactive
   */
  checkActivity() {
    const storedActivityTime = localStorage.getItem('lastActivityTime');
    if (storedActivityTime) {
      const lastActivity = parseInt(storedActivityTime);
      const timeSinceActivity = Date.now() - lastActivity;
      
      // If stored activity is newer than our local time, update
      if (lastActivity > this.lastActivityTime) {
        this.lastActivityTime = lastActivity;
        this.resetTimer();
        
        if (this.isWarningShown) {
          this.isWarningShown = false;
          this.onActivity();
        }
      }
      
      // Check if user should be logged out
      if (timeSinceActivity >= this.timeout) {
        this.triggerInactive();
      }
    }
  }

  /**
   * Manually extend the session
   */
  extendSession() {
    this.handleActivity();
  }
}

export default InactivityDetector;
